/**
 * Tiny analytics helper that wraps GA4 gtag.
 * No-ops if GA4 isn't loaded (so dev/preview without a key, ad-blocked users,
 * or pre-init clicks all stay safe).
 *
 * Standard GA4 recommended events used:
 *   - generate_lead       (Contact form submit, Refer-a-Friend submit)
 *   - text_click          (custom — anyone tapping a sms:/Text Now CTA)
 *   - call_click          (custom — anyone tapping a tel:/Call Now CTA)
 *   - feedback_submit     (custom)
 *   - share_image_create  (custom — anyone using Generate Share Image)
 */

export function track(eventName, params = {}) {
    try {
        if (typeof window === "undefined") return;
        if (typeof window.gtag !== "function") return;
        window.gtag("event", eventName, params);
    } catch {
        // analytics must never break the page
    }
}

/**
 * Global tap-anywhere listener that auto-tracks tel:/sms: link clicks across
 * the whole site without us touching every component. Returns an unmount fn.
 */
export function installGlobalCtaTracking() {
    if (typeof window === "undefined" || window.__lbpCtaTrackerInstalled) {
        return () => {};
    }
    window.__lbpCtaTrackerInstalled = true;

    const handler = (e) => {
        const a = e.target?.closest?.("a[href^='tel:'], a[href^='sms:']");
        if (!a) return;
        const href = a.getAttribute("href") || "";
        const isCall = href.startsWith("tel:");
        const isText = href.startsWith("sms:");
        if (!isCall && !isText) return;

        // Best-effort: try to label which CTA fired
        const label =
            a.getAttribute("data-cta") ||
            a.textContent?.trim().slice(0, 32) ||
            (isCall ? "call_button" : "text_button");

        track(isCall ? "call_click" : "text_click", {
            cta_label: label,
            page_path: window.location?.pathname || "/",
        });
    };

    document.addEventListener("click", handler, { capture: true });
    return () => {
        document.removeEventListener("click", handler, { capture: true });
        delete window.__lbpCtaTrackerInstalled;
    };
}
